/**
 * OneSignal Provider Component
 *
 * Initializes OneSignal and tracks notification permission state. It never
 * prompts on its own: permission is requested only when the user asks for it
 * (see NotificationSettings), so there is no persistent permission banner.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { oneSignalService } from "@/lib/onesignal";
import { useAuthStore } from "@/stores/authStore";
import { useToast } from "@/hooks/use-toast";
import { captureException } from "@/config/sentry";

interface OneSignalContextValue {
  isInitialized: boolean;
  hasPermission: boolean;
  requestPermission: () => Promise<boolean>;
  isSupported: boolean;
}

const OneSignalContext = createContext<OneSignalContextValue | null>(null);

export const useOneSignal = () => {
  const context = useContext(OneSignalContext);
  if (!context) {
    throw new Error("useOneSignal must be used within OneSignalProvider");
  }
  return context;
};

interface OneSignalProviderProps {
  children: ReactNode;
}

export const OneSignalProvider = ({ children }: OneSignalProviderProps) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const { user, isAuthenticated } = useAuthStore();
  const { toast } = useToast();

  const isSupported = oneSignalService.isSupported();

  // Initialize OneSignal on mount
  useEffect(() => {
    const initOneSignal = async () => {
      if (!isSupported) return;

      try {
        await oneSignalService.initialize();
        setIsInitialized(oneSignalService.isReady());

        const permission = await oneSignalService.hasPermission();
        setHasPermission(permission);
      } catch (error) {
        // Push is unavailable (unconfigured App ID, blocked CDN, SW failure).
        // Leave isInitialized false so the UI reflects reality rather than
        // reporting push as ready.
        setIsInitialized(false);
        captureException(error, { scope: "OneSignalProvider.initialize" });
      }
    };

    initOneSignal();
  }, [isSupported]);

  // Set external user ID when user logs in
  useEffect(() => {
    const setUserId = async () => {
      if (!isInitialized || !isAuthenticated || !user?.id) return;

      try {
        await oneSignalService.setExternalUserId(String(user.id));

        const permission = await oneSignalService.hasPermission();
        setHasPermission(permission);
      } catch (error) {
        captureException(error, {
          scope: "OneSignalProvider.setExternalUserId",
        });
      }
    };

    setUserId();
  }, [isInitialized, isAuthenticated, user?.id]);

  // Remove external user ID on logout
  useEffect(() => {
    const handleLogout = async () => {
      if (isInitialized && !isAuthenticated) {
        try {
          await oneSignalService.removeExternalUserId();
          setHasPermission(false);
        } catch (error) {
          captureException(error, { scope: "OneSignalProvider.logout" });
        }
      }
    };

    window.addEventListener("auth:logout", handleLogout);
    return () => window.removeEventListener("auth:logout", handleLogout);
  }, [isInitialized, isAuthenticated]);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    try {
      const currentPermission = oneSignalService.getBrowserPermissionStatus();

      if (currentPermission === "denied") {
        toast({
          title: "Enable notifications in browser settings",
          description:
            "Click the lock icon in your address bar and allow notifications for this site.",
          variant: "destructive",
          duration: 8000,
        });
        return false;
      }

      const granted = await oneSignalService.requestPermission();
      setHasPermission(granted);

      if (granted) {
        toast({
          title: "Notifications enabled",
          description: "You'll now receive important updates and messages.",
        });
      } else {
        const newPermission = oneSignalService.getBrowserPermissionStatus();
        if (newPermission === "denied") {
          toast({
            title: "Enable notifications in browser settings",
            description:
              "Click the lock icon in your address bar and allow notifications for this site.",
            variant: "destructive",
            duration: 8000,
          });
        }
      }

      return granted;
    } catch (error) {
      captureException(error, { scope: "OneSignalProvider.requestPermission" });
      return false;
    }
  }, [toast]);

  const value: OneSignalContextValue = {
    isInitialized,
    hasPermission,
    requestPermission,
    isSupported,
  };

  return (
    <OneSignalContext.Provider value={value}>
      {children}
    </OneSignalContext.Provider>
  );
};
